/* eslint-disable camelcase */

import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  useHvTable,
  useHvPagination,
  useHvSortBy,
  useHvRowExpand,
  HvRowInstance,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import { IncidentContext } from "lib/context/IncidentContext";
import { IncidentSop, useIncident, useIncidentTransactions } from "lib/api";

import SimpleTable, {
  textColumn,
  dateColumn,
  expandColumn,
  progressColumn,
} from "components/common/SimpleTable";

import useStyles from "./styles";
import SopStepDetails from "./SopStepDetails";
import SopStepDialog from "../SopStepDialog";

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 5;

const DEFAULT_SORT = [{ id: "updatedDate", desc: true }];

interface DialogData {
  sopId: string;
  stepIndex: number;
}

interface IncidentSopWithUpdatedDate extends IncidentSop {
  sop_updated_date?: string;
}

const IncidentSops: React.FC = () => {
  const { t } = useTranslation("map");
  const classes = useStyles();

  const columns = useMemo(
    (): HvTableColumnConfig<IncidentSopWithUpdatedDate>[] => [
      expandColumn(
        {
          id: "name",
          Header: t("sections.sop.columns.name") as string,
          accessor: "sop_name",
          style: { width: "30%" },
        },
        t("labels.expandRowButtonAriaLabel"),
        t("labels.collapseRowButtonAriaLabel"),
        () => true
      ),
      textColumn({
        Header: t("sections.sop.columns.description") as string,
        accessor: "sop_desc",
        style: { width: "30%" },
        disableSortBy: true,
      }),
      progressColumn(
        {
          Header: t("sections.sop.columns.progress") as string,
          accessor: "sop_completed_steps",
          style: { width: "25%" },
          disableSortBy: true,
        },
        (row) => row.original.sop_completed_steps as number,
        (row) => row.original.sop_steps.length as number
      ),
      textColumn({
        accessor: "sop_steps",
        style: { width: 80, paddingLeft: 0, textAlign: "right" },
        disableSortBy: true,
        Cell: ({ row }: HvCellProps<IncidentSopWithUpdatedDate, string>) => (
          <HvTypography className={classes.stepsPartialTotal}>
            {`${row.original.sop_completed_steps}/${
              row.original.sop_steps.length
            } ${t("sections.sop.columns.progress_desc")}`}
          </HvTypography>
        ),
      }),
      dateColumn({
        id: "updatedDate",
        Header: t("sections.sop.columns.updatedDate") as string,
        accessor: "sop_updated_date",
        style: { width: 200 },
      }),
    ],
    [classes.stepsPartialTotal, t]
  );

  const { incident: { pk_inc_id = "", inc_sop_map = [] } = {} } =
    useContext(IncidentContext);

  const stepIds = useMemo(
    () =>
      inc_sop_map.reduce<string[][]>((prev, { sop_steps }) => {
        prev.push(
          Object.values(sop_steps).reduce<string[]>((ids, { id = "" }) => {
            ids.push(id);
            return ids;
          }, [])
        );
        return prev;
      }, []),
    [inc_sop_map]
  );

  const { data: sopStepsDetails } = useIncidentTransactions(`${pk_inc_id}`);

  const sopUpdatedDates = useMemo(() => {
    const updatedDates: string[][] = [];
    stepIds.forEach((sopIds) => {
      const dates: string[] = [];
      sopIds.forEach((id) => {
        const sopStepDetail = sopStepsDetails?.data.find(
          (trans) => trans.fk_sop_step_id === id
        );
        if (sopStepDetail) dates.push(sopStepDetail.update_timestamp);
      });
      updatedDates.push(dates);
    });

    return updatedDates.reduce((prev, dates) => {
      prev.push(
        dates.sort(
          (date1, date2) =>
            new Date(date1).getTime() - new Date(date2).getTime()
        )[0]
      );
      return prev;
    }, []);
  }, [stepIds, sopStepsDetails?.data]);

  const sopData = useMemo(() => {
    return inc_sop_map.map((sop, index) => ({
      ...sop,
      sop_updated_date: sopUpdatedDates[index]
        ? sopUpdatedDates[index]
        : sop.sop_initiated_date,
    }));
  }, [inc_sop_map, sopUpdatedDates]);

  const initialState = useMemo(
    () => ({
      pageIndex: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: DEFAULT_SORT,
    }),
    []
  );

  const instance = useHvTable<IncidentSopWithUpdatedDate>(
    {
      columns,
      data: sopData,
      getRowId: useCallback(
        (v: IncidentSopWithUpdatedDate): string => `${v.id}`,
        []
      ),
      pageCount: Math.ceil(
        (inc_sop_map.length ?? 0) / (initialState?.pageSize ?? 1)
      ),
      initialState,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
      autoResetExpanded: false,
    },
    useHvSortBy,
    useHvRowExpand,
    useHvPagination
  );

  const [dialog, setDialog] = useState<DialogData>();

  const openSopStep = useCallback((sopId: string, stepIndex: number) => {
    setDialog({
      sopId,
      stepIndex,
    });
  }, []);

  const { mutate: revalidateIncident } = useIncident(`${pk_inc_id}`, true);
  const closeSopStep = () => {
    setDialog(undefined);
    revalidateIncident();
  };

  const getExpandedRowContent = useCallback(
    (row: HvRowInstance<IncidentSopWithUpdatedDate>) => {
      const { id, sop_steps } = row.original;

      return (
        <SopStepDetails
          sopId={id}
          sopSteps={sop_steps}
          sopStepsDetails={sopStepsDetails?.data}
          openSopStep={openSopStep}
        />
      );
    },
    [openSopStep, sopStepsDetails?.data]
  );

  return (
    <>
      <HvGrid container alignItems="flex-start" style={{ height: 125 }}>
        <HvGrid item xs={12}>
          <HvGlobalActions title={t("sections.sop.title")} variant="section" />
          <HvTypography variant="normalText">
            {t("sections.sop.description")}
          </HvTypography>
        </HvGrid>
      </HvGrid>
      <HvGrid container>
        <HvGrid item xs={12}>
          <SimpleTable
            instance={instance}
            totalCount={inc_sop_map.length}
            disableSelection
            defaultPageSize={DEFAULT_PAGE_SIZE}
            getExpandedRowContent={getExpandedRowContent}
          />
        </HvGrid>
      </HvGrid>
      <SopStepDialog
        isOpen={dialog != null}
        onClose={closeSopStep}
        {...dialog}
      />
    </>
  );
};

export default IncidentSops;
